/* =========================================================================
   Post-game Arcade — replay the four mini-games standalone once a player has
   completed a full game (localStorage `catholics.arcadeUnlocked`). The tiles
   live on the HOME screen; a locked state nudges the player to finish a game.

   Sandboxing: the mini-games award cents through the shared ctx (ctx.engine,
   ctx.updateBank, ctx.floatDelta, ctx.addAchievement) and return control via
   ctx.nextQuestion. In arcade mode we STASH ctx.engine / ctx.game / ctx.nextQuestion,
   swap in a throwaway sandbox engine + game (so awards and achievements land in
   the sandbox, never a real run), and override ctx.nextQuestion to restore
   everything and drop the player back on home. Any mini-game finishing — win,
   skip, or fallback — routes through that single restore, so the player always
   lands home with ctx intact.
   ========================================================================= */
import { ctx } from './context';
import { isAuthenticated, postArcadeResult } from './api';
import { getAuthedHistory } from './progression';
import { RoundEngine, fmtCents } from './engine';
import { ROUNDS } from './data/packs';
import { byId, show } from './dom';
import { track } from './telemetry';
import { showManna } from './minigames/manna';
import { showMatch3 } from './minigames/match3';
import { showMaryMatch3 } from './minigames/maryMatch3';
import { showBonusWheel, resetWheel } from './minigames/wheel';
import { triggerCharPop } from './characters/ui';

const UNLOCK_KEY = 'catholics.arcadeUnlocked';

export type ArcadeGame = 'match3' | 'maryMatch3' | 'manna' | 'wheel';

export function isArcadeUnlocked(): boolean {
  // A signed-in veteran on a fresh device has earned it (server history proves a
  // completed game) — same override the disclosure tier honors (QA audit #3).
  if (isAuthenticated() && getAuthedHistory()) return true;
  try {
    return localStorage.getItem(UNLOCK_KEY) === '1';
  } catch {
    return false;
  }
}

export function unlockArcade(): void {
  try {
    localStorage.setItem(UNLOCK_KEY, '1');
  } catch {
    /* private mode — arcade simply stays locked between reloads */
  }
}

/** Re-lock the Arcade (logout hygiene: sign-out reads as a clean slate; a
    returning guest legitimately re-earns the unlock by finishing a game). */
export function resetArcadeUnlock(): void {
  try {
    localStorage.removeItem(UNLOCK_KEY);
  } catch {
    /* noop */
  }
}

/** Refresh the home Arcade section's locked/unlocked state. Called whenever the
    home screen is shown (an account may have just completed a game). */
export function renderArcade(): void {
  const unlocked = isArcadeUnlocked();
  // The whole section only exists once earned — no locked teaser on the home page.
  byId('arcadeSection').classList.toggle('hidden', !unlocked);
  byId('arcadeLocked').classList.add('hidden'); // teaser retired (markup kept for reuse)
  byId('arcadeGrid').classList.toggle('hidden', !unlocked);
}

/** Wire the arcade tiles once at boot. */
export function initArcade(): void {
  byId('arcadeGrid')
    .querySelectorAll<HTMLButtonElement>('.arc-tile')
    .forEach((b) => b.addEventListener('click', () => playArcade(b.dataset.game as ArcadeGame)));
  renderArcade();
}

/** Play one mini-game in a sandbox, restoring ctx + returning home on finish.

    Cents earned in a play are tallied by the throwaway engine's bankCents. For a
    SIGNED-IN player we post that tally to /game/arcade-result on finish; the server
    credits it to the real wallet, DAILY-CAPPED so the infinitely-replayable arcade
    can't farm points (Eric: "letting people play but no points" → capped points).
    ITEM grants + achievements stay suppressed (ctx.arcadeMode) either way — only
    cents are creditable, and only up to the cap. Guests keep pure practice.

    opts.tester (the /sadmin "Test" deep-link, ?tester=<game>): a pure QA sandbox —
    NO wallet credit even when signed in, NO ArcadePlayed telemetry/quest beat, so
    admin test spins never pollute stats or the daily arcade quest. */
export function playArcade(game: ArcadeGame, opts: { tester?: boolean } = {}): void {
  if (!game) return;
  const tester = opts.tester === true;
  const authed = !tester && isAuthenticated(); // tester: always practice, never credit
  if (tester) track('ArcadeTester', { game });
  else track('ArcadePlayed', { game, authed });

  // Stash the real runtime state.
  const savedEngine = ctx.engine;
  const savedGame = ctx.game;
  const savedNext = ctx.nextQuestion;

  // Sandbox: a throwaway engine (awardBonus only touches bankCents/log) + game
  // state so achievements and cents never corrupt a real run. The pack argument
  // is unused beyond construction — arcade never serves questions.
  ctx.game = { bank: 0, roundIndex: 0, log: [], achievements: [] };
  const sandboxEngine = new RoundEngine(ROUNDS[0].pack.questions, 1, 0);
  ctx.engine = sandboxEngine;
  ctx.arcadeMode = true; // suppress real inventory grants + achievements for sandbox spins

  const restore = (): void => {
    // Capture the play's winnings BEFORE swapping the real engine back in.
    const winnings = authed ? Math.max(0, sandboxEngine.bankCents) : 0;
    ctx.engine = savedEngine;
    ctx.game = savedGame;
    ctx.nextQuestion = savedNext;
    ctx.arcadeMode = false;
    resetWheel(); // leave the wheel neutral for the next arcade/game
    byId('bankbar').classList.add('hidden');
    show('home');
    renderArcade();
    if (authed && winnings > 0) void creditArcadeWinnings(game, winnings);
  };
  ctx.nextQuestion = restore; // every mini-game exit path calls this

  // Signed-in players see their winnings tally (a real, capped reward is coming);
  // guests keep the bank hidden — for them the arcade is pure practice.
  byId('bankbar').classList.toggle('hidden', !authed);
  if (authed) ctx.updateBank();
  if (game === 'manna') showManna();
  else if (game === 'match3') showMatch3();
  else if (game === 'maryMatch3') showMaryMatch3();
  else {
    resetWheel();
    showBonusWheel();
  }
}

/** Post a signed-in arcade play's winnings for a daily-capped wallet credit and
    tell the player what actually landed. Fire-and-forget & fail-soft: if the
    endpoint/migration isn't live yet (offline/503/404) we say nothing rather than
    claim points that never persisted. */
async function creditArcadeWinnings(game: ArcadeGame, winningsCents: number): Promise<void> {
  const uuid =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
  const res = await postArcadeResult({ gameKey: game, winningsCents, idempotencyKey: `arcade:${uuid}` });
  if (!res.ok) return; // guest / offline / endpoint not deployed — honest silence
  const { creditedCents, remainingTodayCents } = res.data;
  try {
    if (creditedCents > 0) {
      const more =
        remainingTodayCents > 0
          ? `${fmtCents(remainingTodayCents)} more to earn here today.`
          : `That's today's limit; more tomorrow.`;
      triggerCharPop(`Banked ${fmtCents(creditedCents)} from the arcade. ${more}`);
    } else {
      triggerCharPop(`That's all the arcade pays today. Play on for fun.`);
    }
  } catch {
    /* no character UI mounted — skip the pop */
  }
}
